import type { HsHypersceneTypes } from '../types/hyperscene-types.type';

export class HsVector3 {
  public readonly values: HsHypersceneTypes.Array3;

  constructor(x: number, y: number, z: number) {
    this.values = [x, y, z];
  }

  /**
   * Handles x.
   * @returns Result value.
   */
  public get x(): number {
    return this.values[0];
  }

  /**
   * Handles y.
   * @returns Result value.
   */
  public get y(): number {
    return this.values[1];
  }

  /**
   * Handles z.
   * @returns Result value.
   */
  public get z(): number {
    return this.values[2];
  }

  /**
   * Sets set.
   * @param x x.
   * @param y y.
   * @param z z.
   * @returns Result value.
   */
  public set(x: number, y: number, z: number): HsVector3 {
    this.values[0] = x;
    this.values[1] = y;
    this.values[2] = z;
    return this;
  }

  /**
   * Sets from array.
   * @param array array.
   * @returns Result value.
   */
  public setFromArray(array: HsHypersceneTypes.Array3): HsVector3 {
    this.values[0] = array[0];
    this.values[1] = array[1];
    this.values[2] = array[2];
    return this;
  }

  /**
   * Sets from matrix position.
   * @returns Result value.
   */
  public setFromMatrixPosition(elements: HsHypersceneTypes.Array16): HsVector3 {
    this.values[0] = elements[12];
    this.values[1] = elements[13];
    this.values[2] = elements[14];
    return this;
  }

  /**
   * Sets from matrix scale.
   * @returns Result value.
   */
  public setFromMatrixScale(elements: HsHypersceneTypes.Array16): HsVector3 {
    this.values[0] = Math.sqrt(elements[0] * elements[0] + elements[1] * elements[1] + elements[2] * elements[2]);
    this.values[1] = Math.sqrt(elements[4] * elements[4] + elements[5] * elements[5] + elements[6] * elements[6]);
    this.values[2] = Math.sqrt(elements[8] * elements[8] + elements[9] * elements[9] + elements[10] * elements[10]);
    return this;
  }

  /**
   * Copies copy.
   * @param other other.
   * @returns Result value.
   */
  public copy(other: HsVector3): HsVector3 {
    return this.set(other.x, other.y, other.z);
  }

  /**
   * Adds add.
   * @param other other.
   * @returns Result value.
   */
  public add(other: HsVector3): HsVector3 {
    return this.set(this.x + other.x, this.y + other.y, this.z + other.z);
  }

  /**
   * Handles subtract.
   * @param other other.
   * @returns Result value.
   */
  public subtract(other: HsVector3): HsVector3 {
    return this.set(this.x - other.x, this.y - other.y, this.z - other.z);
  }

  /**
   * Adds vectors.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public addVectors(a: HsVector3, b: HsVector3): HsVector3 {
    return this.set(a.x + b.x, a.y + b.y, a.z + b.z);
  }

  /**
   * Handles subtract vectors.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public subtractVectors(a: HsVector3, b: HsVector3): HsVector3 {
    return this.set(a.x - b.x, a.y - b.y, a.z - b.z);
  }

  /**
   * Handles multiply by scalar.
   * @param multiplier multiplier.
   * @returns Result value.
   */
  public multiplyByScalar(multiplier: number): HsVector3 {
    return this.set(this.x * multiplier, this.y * multiplier, this.z * multiplier);
  }

  /**
   * Handles divide by scalar.
   * @param devisor devisor.
   * @returns Result value.
   */
  public divideByScalar(devisor: number): HsVector3 {
    return this.set(this.x / devisor, this.y / devisor, this.z / devisor);
  }

  /**
   * Handles normalize.
   * @returns Result value.
   */
  public normalize(): HsVector3 {
    const length: number = Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
    return this.set(this.x / length, this.y / length, this.z / length);
  }

  /**
   * Handles negate.
   * @returns Result value.
   */
  public negate(): HsVector3 {
    return this.set(-this.x, -this.y, -this.z);
  }

  /**
   * Sets from lerp.
   * @param vector1 vector1.
   * @param vector2 vector2.
   * @param ratio ratio.
   * @returns Result value.
   */
  public setFromLerp(vector1: HsVector3, vector2: HsVector3, ratio: number): HsVector3 {
    const x: number = vector1.x * (1 - ratio) + vector2.x * ratio;
    const y: number = vector1.y * (1 - ratio) + vector2.y * ratio;
    const z: number = vector1.z * (1 - ratio) + vector2.z * ratio;
    return this.set(x, y, z);
  }

  /**
   * Handles min.
   * @param other other.
   * @returns Result value.
   */
  public min(other: HsVector3): HsVector3 {
    const x: number = Math.min(this.x, other.x);
    const y: number = Math.min(this.y, other.y);
    const z: number = Math.min(this.z, other.z);
    return this.set(x, y, z);
  }

  /**
   * Handles max.
   * @param other other.
   * @returns Result value.
   */
  public max(other: HsVector3): HsVector3 {
    const x: number = Math.max(this.x, other.x);
    const y: number = Math.max(this.y, other.y);
    const z: number = Math.max(this.z, other.z);
    return this.set(x, y, z);
  }

  /**
   * Handles clamp.
   * @param minVector min vector.
   * @param maxVector max vector.
   * @returns Result value.
   */
  public clamp(minVector: HsVector3, maxVector: HsVector3): HsVector3 {
    const x: number = Math.min(maxVector.x, Math.max(this.x, minVector.x));
    const y: number = Math.min(maxVector.y, Math.max(this.y, minVector.y));
    const z: number = Math.min(maxVector.z, Math.max(this.z, minVector.z));
    return this.set(x, y, z);
  }

  /**
   * Handles apply matrix3.
   * @param matrixValues matrix values.
   * @returns Result value.
   */
  public applyMatrix3(matrixValues: HsHypersceneTypes.Array9): HsVector3 {
    const x: number = this.x;
    const y: number = this.y;
    const z: number = this.z;
    this.values[0] = matrixValues[0] * x + matrixValues[3] * y + matrixValues[6] * z;
    this.values[1] = matrixValues[1] * x + matrixValues[4] * y + matrixValues[7] * z;
    this.values[2] = matrixValues[2] * x + matrixValues[5] * y + matrixValues[8] * z;
    return this;
  }

  /**
   * Handles apply matrix4.
   * @param matrixValues matrix values.
   * @returns Result value.
   */
  public applyMatrix4(matrixValues: HsHypersceneTypes.Array16): HsVector3 {
    const x: number = this.x;
    const y: number = this.y;
    const z: number = this.z;
    const w: number = 1 / (matrixValues[3] * x + matrixValues[7] * y + matrixValues[11] * z + matrixValues[15]);
    this.values[0] = (matrixValues[0] * x + matrixValues[4] * y + matrixValues[8] * z + matrixValues[12]) * w;
    this.values[1] = (matrixValues[1] * x + matrixValues[5] * y + matrixValues[9] * z + matrixValues[13]) * w;
    this.values[2] = (matrixValues[2] * x + matrixValues[6] * y + matrixValues[10] * z + matrixValues[14]) * w;
    return this;
  }

  /**
   * Handles apply quaternion.
   * @param quaternion quaternion.
   * @returns Result value.
   */
  public applyQuaternion(quaternion: HsHypersceneTypes.Array4): HsVector3 {
    const x: number = this.x;
    const y: number = this.y;
    const z: number = this.z;

    const qx: number = quaternion[0];
    const qy: number = quaternion[1];
    const qz: number = quaternion[2];
    const qw: number = quaternion[3];

    const ix: number = qw * x + qy * z - qz * y;
    const iy: number = qw * y + qz * x - qx * z;
    const iz: number = qw * z + qx * y - qy * x;
    const iw: number = -qx * x - qy * y - qz * z;

    this.values[0] = ix * qw + iw * -qx + iy * -qz - iz * -qy;
    this.values[1] = iy * qw + iw * -qy + iz * -qx - ix * -qz;
    this.values[2] = iz * qw + iw * -qz + ix * -qy - iy * -qx;

    return this;
  }

  /**
   * Handles unproject.
   * @param projectionMatrixInverse projection matrix inverse.
   * @param cameraMatrixWorld camera matrix world.
   * @returns Result value.
   */
  public unproject(
    projectionMatrixInverse: HsHypersceneTypes.Array16,
    cameraMatrixWorld: HsHypersceneTypes.Array16
  ): HsVector3 {
    return this.applyMatrix4(projectionMatrixInverse).applyMatrix4(cameraMatrixWorld);
  }

  /**
   * Handles cross.
   * @param other other.
   * @returns Result value.
   */
  public cross(other: HsVector3): HsVector3 {
    return this.crossVectors(this, other);
  }

  /**
   * Handles cross vectors.
   * @param first first.
   * @param second second.
   * @returns Result value.
   */
  public crossVectors(first: HsVector3, second: HsVector3): HsVector3 {
    const firstX: number = first.x;
    const firstY: number = first.y;
    const firstZ: number = first.z;
    const secondX: number = second.x;
    const secondY: number = second.y;
    const secondZ: number = second.z;
    this.values[0] = firstY * secondZ - firstZ * secondY;
    this.values[1] = firstZ * secondX - firstX * secondZ;
    this.values[2] = firstX * secondY - firstY * secondX;
    return this;
  }

  /**
   * Handles absolute.
   * @returns Result value.
   */
  public absolute(): HsVector3 {
    this.values[0] = Math.abs(this.values[0]);
    this.values[1] = Math.abs(this.values[1]);
    this.values[2] = Math.abs(this.values[2]);
    return this;
  }

  /**
   * Handles multiply.
   * @returns Result value.
   */
  public multiply(other: HsVector3): HsVector3 {
    this.values[0] *= other.values[0];
    this.values[1] *= other.values[1];
    this.values[2] *= other.values[2];
    return this;
  }

  /**
   * Handles divide.
   * @returns Result value.
   */
  public divide(other: HsVector3): HsVector3 {
    this.values[0] /= other.values[0];
    this.values[1] /= other.values[1];
    this.values[2] /= other.values[2];
    return this;
  }

  /**
   * Handles inverse.
   * @returns Result value.
   */
  public inverse(): HsVector3 {
    this.values[0] = 1 / this.values[0];
    this.values[1] = 1 / this.values[1];
    this.values[2] = 1 / this.values[2];
    return this;
  }

  /**
   * Sets random.
   * @returns Result value.
   */
  public setRandom(): HsVector3 {
    const u: number = Math.random();
    const v: number = Math.random();
    const theta: number = 2 * Math.PI * u;
    const phi: number = Math.acos(2 * v - 1);
    const x: number = Math.sin(phi) * Math.cos(theta);
    const y: number = Math.sin(phi) * Math.sin(theta);
    const z: number = Math.cos(phi);
    return this.set(x, y, z);
  }

  /**
   * Handles is zero.
   * @returns Result value.
   */
  public isZero(): boolean {
    return this.x === 0 && this.y === 0 && this.z === 0;
  }

  /**
   * Handles length squared.
   * @returns Result value.
   */
  public lengthSquared(): number {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }

  /**
   * Handles length.
   * @returns Result value.
   */
  public length(): number {
    return Math.sqrt(this.lengthSquared());
  }

  /**
   * Builds zero vector.
   * @returns Result value.
   */
  public static makeZeroVector(): HsVector3 {
    return new HsVector3(0, 0, 0);
  }

  /**
   * Builds identity vector.
   * @returns Result value.
   */
  public static makeIdentityVector(): HsVector3 {
    return new HsVector3(1, 1, 1);
  }

  /**
   * Builds unit vector x.
   * @returns Result value.
   */
  public static makeUnitVectorX(): HsVector3 {
    return new HsVector3(1, 0, 0);
  }

  /**
   * Builds unit vector y.
   * @returns Result value.
   */
  public static makeUnitVectorY(): HsVector3 {
    return new HsVector3(0, 1, 0);
  }

  /**
   * Builds unit vector z.
   * @returns Result value.
   */
  public static makeUnitVectorZ(): HsVector3 {
    return new HsVector3(0, 0, 1);
  }

  /**
   * Adds add.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public static add(a: HsVector3, b: HsVector3): HsVector3 {
    return new HsVector3(a.x + b.x, a.y + b.y, a.z + b.z);
  }

  /**
   * Handles subtract.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public static subtract(a: HsVector3, b: HsVector3): HsVector3 {
    return new HsVector3(a.x - b.x, a.y - b.y, a.z - b.z);
  }

  /**
   * Handles lerp.
   * @param a a.
   * @param b b.
   * @param ratio ratio.
   * @returns Result value.
   */
  public static lerp(a: HsVector3, b: HsVector3, ratio: number): HsVector3 {
    const x: number = a.x * (1 - ratio) + b.x * ratio;
    const y: number = a.y * (1 - ratio) + b.y * ratio;
    const z: number = a.z * (1 - ratio) + b.z * ratio;
    return new HsVector3(x, y, z);
  }

  /**
   * Handles average.
   * @param a a.
   * @param b b.
   * @param result result.
   * @returns Result value.
   */
  public static average(a: HsVector3, b: HsVector3, result: HsVector3): HsVector3 {
    result.set((a.x + b.x) * 0.5, (a.y + b.y) * 0.5, (a.z + b.z) * 0.5);
    return result;
  }

  /**
   * Handles dot.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public static dot(a: HsVector3, b: HsVector3): number {
    return a.x * b.x + a.y * b.y + a.z * b.z;
  }

  /**
   * Handles distance squared.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public static distanceSquared(a: HsVector3, b: HsVector3): number {
    const dx: number = a.x - b.x;
    const dy: number = a.y - b.y;
    const dz: number = a.z - b.z;
    return dx * dx + dy * dy + dz * dz;
  }

  /**
   * Handles distance.
   * @param a a.
   * @param b b.
   * @returns Result value.
   */
  public static distance(a: HsVector3, b: HsVector3): number {
    return Math.sqrt(HsVector3.distanceSquared(a, b));
  }

  /**
   * Handles angle between.
   * @param firstVector first vector.
   * @param secondVector second vector.
   * @returns Result value.
   */
  public static angleBetween(firstVector: HsVector3, secondVector: HsVector3): number {
    const lengthsProduct: number = Math.sqrt(firstVector.lengthSquared() * secondVector.lengthSquared());

    if (lengthsProduct === 0) return Math.PI / 2;

    const ratio: number = HsVector3.dot(firstVector, secondVector) / lengthsProduct;

    return Math.acos(Math.max(-1, Math.min(1, ratio)));
  }

  /**
   * Builds copy.
   * @param source source.
   * @returns Result value.
   */
  public static makeCopy(source: HsVector3): HsVector3 {
    return new HsVector3(source.x, source.y, source.z);
  }
}
